import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";

// example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
// context
import {
  useSoftUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { styled } from "@mui/material/styles";
// Images
import team2 from "assets/images/logo-admin.png";
import logoSpotify from "assets/images/small-logos/logo-spotify.svg";
import { useSelector } from "react-redux";
import { authSelector } from "./../../../redux/selector";
import { Badge, Tooltip } from "@mui/material";
import { useDispatch } from "react-redux";
import { logout } from "./../../../redux/slice/authSlice";
import { timeAgo } from "utils";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const { user } = useSelector(authSelector);
  const dispatchRedux = useDispatch();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNoti, setSelectedNoti] = useState({});
  const navigation = useNavigate();

  const handleClickOpen = (item) => {
    setOpen(true);
    setSelectedNoti(item);
    handleCloseMenu();
  };

  const handleRead = async () => {
    // setSelectedNoti({});
    setOpen(false);
    getNotification();
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    getNotification();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      (async () => {
        getNotification();
      })();
    }, 10000);

    return () => clearInterval(id);
  }, []);

  const getNotification = async () => {
    setNotifications(
      new Array(Math.ceil(Math.random() * 100)).fill({
        title: "Bạn có một thông báo mới !",
        content:
          "Lorem, ipsum dolor sit amet consectetur adipisicing elit. Eius atque modi doloribus, a unde facere ad inventore voluptates odit vel provident officiis incidunt quidem fugiat sint doloremque magni eveniet voluptatibus?",
      })
    );
  };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleLogout = () => {
    dispatchRedux(logout());
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2, maxHeight: 500 }}
    >
      {notifications &&
        notifications.length > 0 &&
        notifications.map((noti, index) => (
          <NotificationItem
            key={index}
            image={<img src={team2} alt="person" />}
            title={noti.title}
            content={noti.content}
            date={timeAgo(noti.createdAt)}
            onClick={() => handleClickOpen(noti)}
          />
        ))}
    </Menu>
  );

  const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      right: -3,
      top: 0,
      border: `2px solid ${theme.palette.background.paper}`,
      padding: "0 4px",
    },
  }));

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <SoftBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          {/* <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} /> */}
          <Icon fontSize="medium" sx={navbarDesktopMenu} onClick={handleMiniSidenav}>
            {miniSidenav ? "menu_open" : "menu"}
          </Icon>
        </SoftBox>
        {isMini ? null : (
          <SoftBox sx={(theme) => navbarRow(theme, { isMini })}>
            <SoftBox color={light ? "white" : "inherit"}>
              <IconButton sx={navbarIconButton} size="small">
                <Icon
                  sx={({ palette: { dark, white } }) => ({
                    color: light ? white.main : dark.main,
                  })}
                >
                  account_circle
                </Icon>
                <SoftTypography
                  variant="button"
                  fontWeight="medium"
                  color={light ? "white" : "dark"}
                >
                  {user && Object.keys(user).length > 0
                    ? ` ${user?.name || user?.email || user?.phone}`
                    : " Đăng nhập"}
                </SoftTypography>
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon className={light ? "text-white" : "text-dark"}>
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="small"
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton> */}
              {/* <IconButton
                size="medium"
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <StyledBadge badgeContent={notifications.length} color="info">
                  <Icon className={light ? "text-white" : "text-dark"}>notifications</Icon>
                </StyledBadge>
              </IconButton> */}

              {renderMenu()}

              <IconButton onClick={handleLogout}>
                <SoftTypography
                  variant="body1"
                  color="primary"
                  sx={{ cursor: "pointer", lineHeight: 0 }}
                >
                  <Tooltip title="Đăng xuất" placement="top">
                    <Icon>logout</Icon>
                  </Tooltip>
                </SoftTypography>
              </IconButton>
            </SoftBox>
          </SoftBox>
        )}
      </Toolbar>
      <Dialog
        open={open}
        onClose={handleRead}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <p>{selectedNoti.title}</p>
          <p style={{ fontSize: 14 }}>{timeAgo(selectedNoti.createdAt)}</p>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {selectedNoti.content}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} autoFocus>
            Bỏ qua
          </Button>
          <Button onClick={handleRead} autoFocus>
            Điều hành
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
